import React, { cloneElement, FC, MutableRefObject, ReactElement, useEffect } from 'react'
import { View } from 'react-native'

import { AccordionProps } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/accordion/Accordion'

export type AccordionGroupProps = {
  accordions: ReactElement<AccordionProps>[]
  allowMultiExpand?: boolean
  initialExpandedAccordion?: number
  expandedAccordionsRef?: MutableRefObject<Set<number>>
  onAccordionCollapsed?: (index: number) => void
}

export const AccordionGroup: FC<AccordionGroupProps> = ({
  accordions,
  allowMultiExpand = false,
  initialExpandedAccordion,
  expandedAccordionsRef,
  onAccordionCollapsed,
}) => {
  const [expandedAccordions, setExpandedAccordions] = React.useState<Set<number>>(
    new Set(
      typeof initialExpandedAccordion === 'number' && initialExpandedAccordion >= 0 ? [initialExpandedAccordion] : [],
    ),
  )

  useEffect(() => {
    if (expandedAccordionsRef) {
      expandedAccordionsRef.current = expandedAccordions
    }
  }, [expandedAccordions, expandedAccordionsRef])

  const handleAccordionClicked = (index: number, _isOpen: boolean) => {
    const updatedExpandedAccordions = new Set(expandedAccordions)
    if (updatedExpandedAccordions.has(index)) {
      updatedExpandedAccordions.delete(index)
      if (onAccordionCollapsed) {
        onAccordionCollapsed(index)
      }
    } else if (updatedExpandedAccordions.size > 0 && !allowMultiExpand) {
      // Call onAccordionCollapsed for each accordion that will be collapsed
      if (onAccordionCollapsed) {
        updatedExpandedAccordions.forEach((expandedIndex) => {
          onAccordionCollapsed(expandedIndex)
        })
      }
      updatedExpandedAccordions.clear()
      updatedExpandedAccordions.add(index)
    } else {
      updatedExpandedAccordions.add(index)
    }
    setExpandedAccordions(updatedExpandedAccordions)
  }

  return (
    <View>
      {accordions.map((accordion, index) => {
        return cloneElement(accordion as ReactElement<any>, {
          handleIsExpandedChanged: (isOpen: boolean) => handleAccordionClicked(index, isOpen),
          isExpanded: expandedAccordions.has(index),
        })
      })}
    </View>
  )
}
